import { FC, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

interface Feature {
  icon: React.ReactNode
  title: string
  description: string
}

interface FeatureCarouselProps {
  features: Feature[]
}

export const FeatureCarousel: FC<FeatureCarouselProps> = ({ features }) => {
  const [currentSlide, setCurrentSlide] = useState(0)

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % features.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + features.length) % features.length)
  }

  return (
    <div className="flex flex-col items-center justify-center h-full px-12 text-white">
      {/* Feature Content */}
      <div className="flex flex-col items-center text-center mb-12">
        <div className="mb-8">
          <div className="w-[70px] h-[70px] rounded-2xl flex items-center justify-center relative">
            <div
              className="w-[70px] h-[70px] rounded-2xl bg-[linear-gradient(180deg,rgba(17,108,23,0.27)_0%,rgba(48,255,103,0.27)_48.08%,rgba(157,255,0,0.27)_72.12%,rgba(255,255,255,0.27)_100%)] blur-[45.95px] flex items-center justify-center absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(17, 108, 23, 0.27) 0%, rgba(48, 255, 103, 0.27) 48.08%, rgba(157, 255, 0, 0.27) 72.12%, rgba(255, 255, 255, 0.27) 100%)',
              }}
            />
            {features[currentSlide].icon}
          </div>
        </div>

        <h2 className="text-title-m font-medium mb-6 max-w-md">
          {features[currentSlide].title}
        </h2>

        <p className="text-[18px] font-normal leading-[22px] text-[#A9A9A9] max-w-lg tracking-[-3%]">
          {features[currentSlide].description}
        </p>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-4">
        <Button
          onClick={prevSlide}
          variant="tertiary"
          className="h-[32px] rounded-full"
          aria-label="Previous slide"
        >
          <Icon
            icon="chevron-down"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className="text-white rotate-90"
          />
        </Button>

        {/* Dots Indicator */}
        <div className="flex gap-2 items-center bg-monochrome-4-60 border border-monochrome-120 rounded-full p-[10px] px-[14px] backdrop-blur-[21.75px]">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`rounded-full transition-all ${
                index === currentSlide
                  ? 'w-3 h-3 bg-green-400'
                  : 'w-2 h-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <Button
          onClick={nextSlide}
          variant="tertiary"
          className="h-[32px] rounded-full"
          aria-label="Next slide"
        >
          <Icon
            icon="chevron-down"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className="text-white rotate-270"
          />
        </Button>
      </div>
    </div>
  )
}
